import React from "react";
import { Link } from "react-router-dom";
import { Heart, ChevronRight, Camera, BookOpen, Sparkles, Feather, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import Countdown from "@/components/Countdown";
import { useAuth } from "@/lib/AuthContext";

const TILES = [
  { to: "/histoire", icon: Calendar, label: "Notre histoire", desc: "Les moments qui comptent." },
  { to: "/galerie", icon: Camera, label: "Galerie", desc: "Photos & vidéos de nous." },
  { to: "/mots", icon: Heart, label: "Mots d'amour", desc: "Petites pensées." },
  { to: "/lettre", icon: BookOpen, label: "Lettre", desc: "Une lettre pour toi." },
  { to: "/ton-espace", icon: Feather, label: "Ton espace", desc: "Écris-moi." },
];

export default function HomePage() {
  const { isAdmin } = useAuth();
  const tiles = [...TILES];
  if (isAdmin) {
    tiles.splice(3, 0, { to: "/surprise", icon: Sparkles, label: "Surprise", desc: "Scellée jusqu'au 22 juin.", admin: true });
  }

  return (
    <>
      <section className="relative min-h-[calc(100vh-72px)] overflow-hidden grain">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1543829969-57899edf981b?crop=entropy&cs=srgb&fm=jpg&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.35) saturate(1.1)",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0F0407]/40 via-[#0F0407]/60 to-[#0F0407]" />

        <div className="px-6 md:px-12 lg:px-24 pt-16 lg:pt-24 pb-24">
          <div className="max-w-4xl">
            <p className="font-accent text-4xl md:text-5xl text-rose-300 mb-4 reveal">notre histoire d'amour</p>
            <h1
              className="font-serif text-5xl sm:text-6xl lg:text-8xl text-rose-50 tracking-tight leading-[1.05] reveal"
              style={{ animationDelay: "0.15s" }}
            >
              Chaque seconde,
              <br />
              <em className="text-rose-200/90">un battement.</em>
            </h1>
            <p className="text-rose-100/80 mt-8 max-w-xl text-lg leading-relaxed reveal" style={{ animationDelay: "0.3s" }}>
              Le compte à rebours est lancé. Rendez-vous le{" "}
              <span className="text-rose-300 font-medium">lundi 22 juin 2026</span> à{" "}
              <span className="text-rose-300 font-medium">15h00</span>.
            </p>
          </div>

          <div className="mt-14 lg:mt-20 reveal" style={{ animationDelay: "0.45s" }}>
            <Countdown />
          </div>

          <div className="mt-16 flex flex-wrap gap-4 reveal" style={{ animationDelay: "0.6s" }}>
            <Link to="/galerie">
              <Button
                data-testid="cta-gallery"
                className="bg-rose-600 hover:bg-rose-500 text-white rounded-full h-12 px-7 hover:shadow-[0_0_30px_rgba(230,57,70,0.45)]"
              >
                <Camera className="w-4 h-4 mr-2" />
                Voir la galerie
              </Button>
            </Link>
            <Link to="/lettre">
              <Button
                data-testid="cta-letter"
                variant="outline"
                className="border-rose-700/60 text-rose-100 hover:bg-rose-900/30 hover:text-rose-50 rounded-full h-12 px-7"
              >
                Lire la lettre
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="font-accent text-3xl text-rose-300">explore</p>
            <h2 className="font-serif text-4xl md:text-5xl text-rose-50 mt-2">Tout, en un seul endroit</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tiles.map((t) => (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`tile-${t.to.replace(/\//g, "")}`}
                className={`group romantic-card rounded-2xl p-7 flex items-start gap-4 transition-all hover:-translate-y-1 ${
                  t.admin ? "ring-1 ring-amber-700/40" : ""
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-rose-900/40 flex items-center justify-center shrink-0 group-hover:bg-rose-700/50 transition">
                  <t.icon className="w-5 h-5 text-rose-200" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-serif text-2xl text-rose-50">{t.label}</h3>
                    {t.admin && (
                      <span className="text-[10px] uppercase tracking-wider text-amber-300 px-2 py-0.5 rounded-full bg-amber-900/30 border border-amber-700/40">
                        admin
                      </span>
                    )}
                  </div>
                  <p className="text-rose-200/70 text-sm mt-1">{t.desc}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-rose-400/60 group-hover:text-rose-300 group-hover:translate-x-1 transition" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
